import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RefreshCw, User } from 'lucide-react';

interface TemplateVariableEditorProps {
  variables: string[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onResetToDefaults?: () => void;
  className?: string;
}

// Common variable descriptions
const VARIABLE_DESCRIPTIONS: Record<string, string> = {
  UserName: 'Nome do usuário',
  Name: 'Nome do usuário',
  Email: 'Email do usuário',
  Username: 'Username do usuário',
  ConfirmationURL: 'URL de confirmação',
  SiteURL: 'URL do site',
  SiteUrl: 'URL do site',
  Token: 'Token de verificação',
  TokenHash: 'Hash do token',
  LikesCount: 'Quantidade de likes',
  ViewsCount: 'Quantidade de visualizações',
  DaysInactive: 'Dias inativo',
  UnsubscribeUrl: 'URL de descadastro',
  UnreadMessagesCount: 'Mensagens não lidas',
  Date: 'Data',
  PlanName: 'Nome do plano',
  DaysRemaining: 'Dias restantes',
  ExpirationDate: 'Data de expiração',
  RenewalUrl: 'URL de renovação',
  EngagementMessage: 'Mensagem de engajamento',
  CtaText: 'Texto do botão CTA',
  NewProfilesCount: 'Novos perfis',
  MatchName: 'Nome do match',
  LikerName: 'Nome de quem curtiu',
  SenderName: 'Nome do remetente',
  MessagePreview: 'Preview da mensagem',
  Reason: 'Motivo',
};

// Helper to extract variables from HTML content
export const extractVariablesFromHtml = (htmlContent: string): string[] => {
  const regex = /\{\{\s*\.?(\w+)\s*\}\}/g;
  const matches = new Set<string>();
  let match;
  while ((match = regex.exec(htmlContent)) !== null) {
    matches.add(match[1]);
  }
  return Array.from(matches);
};

const TemplateVariableEditor: React.FC<TemplateVariableEditorProps> = ({
  variables,
  values,
  onChange,
  onResetToDefaults,
  className = '',
}) => {
  if (variables.length === 0) {
    return (
      <div className={`text-white/40 text-sm text-center py-4 ${className}`}>
        Nenhuma variável encontrada no template
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-white/80 text-sm font-medium">
          Variáveis do Template ({variables.length})
        </h4>
        {onResetToDefaults && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetToDefaults}
            className="text-white/60 hover:text-white text-xs"
          >
            <User className="h-3 w-3 mr-1" />
            Resetar para dados do usuário
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {variables.map((variable) => (
          <div key={variable} className="space-y-1">
            <Label className="text-white/60 text-xs flex items-center gap-1">
              <span className="text-luxury-pinkBright font-mono">{'{{'}.</span>
              {variable}
              <span className="text-luxury-pinkBright font-mono">{'}}'}</span>
            </Label>
            <Input
              value={values[variable] || ''}
              onChange={(e) => onChange(variable, e.target.value)}
              placeholder={VARIABLE_DESCRIPTIONS[variable] || variable}
              className="bg-white/5 border-white/10 text-white text-sm h-8"
            />
            {VARIABLE_DESCRIPTIONS[variable] && (
              <p className="text-white/30 text-xs">{VARIABLE_DESCRIPTIONS[variable]}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateVariableEditor;
